import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  fetchThroughSearchAsync,
  setSubCategories,
} from "../../features/listingPage/listingPageSlice";
import { setSelectedSubcategory } from "../../store/slices/subCategorySlice";
import { setBreadcrumb } from "../../store/slices/breadCrumbsSlice";

const MobilePopularSearch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openSection, setOpenSection] = useState(null);

  const handleSearchNavigate = (value) => {
    dispatch(fetchThroughSearchAsync(value.trim()));
    dispatch(setSubCategories(null));
    dispatch(setSelectedSubcategory(null));
    dispatch(
      setBreadcrumb({
        categoryID: 0,
        categoryName: "",
        subCategoryID: 0,
        subCategoryName: "",
        ProductID: 0,
        productName: "",
      })
    );
    navigate(`/search/${value}`);
  };

  const toggleSection = (name) => {
    setOpenSection(openSection === name ? null : name);
  };

  // 🔹 Organized data for cleaner mapping
  const categories = [
    {
      name: "Cockpit",
      items: ["Handlebar", "Stem", "Seatpost", "Grips", "Headset", "Pedal"],
    },
    {
      name: "Drivetrain",
      items: [
        "Bottom Bracket",
        "Chain",
        "Crankset",
        "Cassette",
        "Derailleur",
        "Freewheel",
        "Chainring",
      ],
    },
    {
      name: "Hydration",
      items: ["Bottles", "Bottle Holders"],
    },
    {
      name: "Helmets",
      items: ["MTB", "Road", "City", "Enduro"],
    },
    {
      name: "Wheels & Tires",
      items: ["Wheel", "Rim", "Hub", "Axles & QR"],
    },
    {
      name: "Cable & Housing",
      items: ["Brake housing", "Brake wire"],
    },
    {
      name: "Brakes & Brake Parts",
      items: ["Brake Lever", "Disc Brake", "Disc Rotor"],
    },
    {
      name: "Lights",
      items: ["Front Light", "Rear Light"],
    },
    {
      name: "Small Parts",
      items: ["Chain Guide", "Bearings & Spacers", "Clamps"],
    },
    {
      name: "Pumps & Inflation",
      items: ["Air Pump", "Cartridges"],
    },
    {
      name: "Stands & Storage",
      items: ["Stands", "Storage", "Mounts"],
    },
    {
      name: "Clothing",
      items: ["Cycling Socks", "Tshirts"],
    },
    {
      name: "Sealants & Tubeless Kits",
      items: ["Kit & Valves", "Rim Tapes"],
    },
    {
      name: "Tools & Maintenance",
      items: ["Tools"],
    },
    {
      name: "Fork",
      items: ["Suspension Fork"],
    },
  ];

  const popularSearches = [
    "Parts",
    "Accessories",
    "Helmets",
    "Maintenance",
    "Lights",
    "Cycling Tools",
    "MTB Wheelset",
    "Cycling Clothes",
    "Spare Parts",
    "Best MTB Parts",
    "Mini Tools",
    "Bicycle Gears",
    "Kids Bikes",
    "MTB Bikes",
  ];

  return (
    <div className="w-full bg-gradient-to-b from-blue to-blue text-white-1 p-4 shadow-lg">
      {/* Header */}
      <h3 className="text-2xl font-extrabold tracking-wide mb-3">
        Popular Searches
      </h3>

      {/* 🌟 Responsive Popular Search Tags Grid */}
      <div className="w-full mt-3">
        <div className="flex flex-wrap gap-3 justify-start">
          {popularSearches.map((term, i) => (
            <motion.span
              key={i}
              whileTap={{ scale: 0.93 }}
              whileHover={{ scale: 1.05 }}
              onClick={() => handleSearchNavigate(term.toLowerCase())}
              className="px-4 py-[6px] bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-full border border-white/20 text-[13px] font-semibold tracking-wide text-white cursor-pointer transition-all duration-300 shadow-sm hover:shadow-md active:shadow-inner"
            >
              {term}
            </motion.span>
          ))}
        </div>
      </div>

      {/* Accordion Sections */}
      <div className="mt-6 space-y-3">
        {categories.map((cat) => (
          <div
            key={cat.name}
            className="bg-white/10 rounded-xl overflow-hidden backdrop-blur-md border border-white/10"
          >
            <button
              onClick={() => toggleSection(cat.name)}
              className="w-full flex justify-between items-center px-4 py-3 font-semibold text-base uppercase tracking-wide hover:bg-white/10 transition"
            >
              <span className="text-gold font-bold">{cat.name}</span>
              <span
                className={`transition-transform duration-300 ${
                  openSection === cat.name ? "rotate-180" : "rotate-0"
                }`}
              >
                ▼
              </span>
            </button>

            <AnimatePresence>
              {openSection === cat.name && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="px-4 pb-3 flex flex-wrap gap-x-3 gap-y-2 text-sm"
                >
                  {cat.items.map((item, i) => (
                    <motion.span
                      key={i}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => handleSearchNavigate(item.toLowerCase())}
                      className="text-white-1/90 border border-white-1/20 rounded-full px-3 py-0.5 hover:text-white-1 cursor-pointer font-medium"
                    >
                      {item}
                    </motion.span>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MobilePopularSearch;
